import React from 'react';
import { useLocation } from 'react-router-dom';
import { useOrgBranding } from '../../hooks/useOrgBranding';

/**
 * Organization footer shown at the bottom of the org-admin, client and agency layouts.
 * Black background, white text, with the organization's white logo centered on top.
 * Content (logo + data) comes from the current user's organization branding settings.
 */
const Footer: React.FC = () => {
  const { branding, loading } = useOrgBranding();
  const { pathname } = useLocation();

  // Detail/split views need the full height — hide the footer on casting detail,
  // production detail, and any models view.
  const hideOnDetail =
    /\/castings\/\d+/.test(pathname) ||
    /\/productions\/\d+/.test(pathname) ||
    /\/models(\/|$)/.test(pathname);
  if (hideOnDetail) return null;

  if (loading || !branding) return null;

  const { logo_white_url, company_name, address, phone, email, legal } = branding;

  // Nothing configured yet — don't render an empty bar.
  const hasContent = logo_white_url || company_name || address || phone || email || legal;
  if (!hasContent) return null;

  return (
    <footer className="bg-black text-white mt-auto flex-shrink-0">
      <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-center">
        {logo_white_url && (
          <img
            src={logo_white_url}
            alt={company_name || 'Organization logo'}
            className="h-6 w-auto object-contain"
          />
        )}
        {company_name && <span className="text-xs font-semibold tracking-wide">{company_name}</span>}
        {address && <span className="text-xs text-gray-400">{address.replace(/\n+/g, ', ')}</span>}
        {phone && <span className="text-xs text-gray-400">{phone}</span>}
        {email && (
          <a href={`mailto:${email}`} className="text-xs text-gray-400 hover:text-white underline-offset-2 hover:underline">
            {email}
          </a>
        )}
        {legal && <span className="text-[11px] text-gray-500">{legal.replace(/\n+/g, ' · ')}</span>}
      </div>
    </footer>
  );
};

export default Footer;
